// src/components/DocRevisionHistory.js
import React, { useRef, useState } from "react";
import DocFinalizeModal from "./DocFinalizeModal";

/**
 * DocRevisionHistory
 * - Selected revision details with safe restore and row actions
 *   (View / Restore / Edit / Save / Preview PDF / Finalize).
 * - Revision selection and ordering are controlled by the Editor page tabs.
 *
 * Props:
 *  - items: Array<{
 *      id: string,
 *      label: string,
 *      summary?: string,
 *      type?: "draft"|"revision"|"final",
 *      createdAt?: string|number|Date,
 *      wordCount?: number,
 *      userEdited?: boolean
 *    }>
 *  - currentId?: string
 *  - locked?: boolean
 *
 *  - onView?:         (id:string)=>void
 *  - onRestore?:      (id:string)=>void
 *  - onRename?:       (id:string, newLabel:string)=>void
 *  - onExport?:       (id:string, format:"pdf"|"docx"|"text")=>void
 *  - onDelete?:       (id:string)=>void
 *  - onRequestEdit?:  (id:string)=>void
 *
 *  - onSave?:        (id:string)=>Promise<void>|void
 *  - onPreviewPdf?:  (id:string)=>Promise<void>|void
 *  - onFinalize?: (
 *      id:string,
 *      format:"pdf"|"docx"|"text"
 *    )=>Promise<void>|void
 */
const DocRevisionHistory = ({
  items = [],
  currentId,
  locked = false,
  onView,
  onRestore,
  onRename,
  onExport,
  onDelete,
  onRequestEdit,
  onSave,
  onPreviewPdf,
  onFinalize,
}) => {
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);

  // Finalize modal
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeForId, setFinalizeForId] = useState(null);

  // Per-row pending states
  const [savingId, setSavingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [finalizingId, setFinalizingId] = useState(null);

  // Block UI while the Editor page performs authoritative finalization.
  const [blocking, setBlocking] = useState(false);

  // Prevent accidental duplicate actions while keeping the first click responsive.
  const pendingActionsRef = useRef(new Set());

  const beginAction = (key) => {
    if (pendingActionsRef.current.has(key)) {
      return false;
    }

    pendingActionsRef.current.add(key);
    return true;
  };

  const endAction = (key) => {
    pendingActionsRef.current.delete(key);
  };

  // Unified feedback for mouse, touch, and pen.
  const [pressedKey, setPressedKey] = useState(null);

  const isPressed = (key) => pressedKey === key;

  const pressableHandlers = (key) => ({
    onPointerDown: (event) => {
      if (
        event.pointerType !== "mouse" ||
        event.button === 0
      ) {
        setPressedKey(key);
      }
    },
    onPointerUp: () => setPressedKey(null),
    onPointerCancel: () => setPressedKey(null),
    onPointerLeave: () => setPressedKey(null),
    onBlur: () => setPressedKey(null),
  });

  // ---------- Edit / Save / Preview / Finalize handlers ----------

  const handleEdit = (revId) => {
    if (
      locked ||
      typeof onRequestEdit !== "function"
    ) {
      return;
    }

    const actionKey = `edit:${revId}`;

    if (!beginAction(actionKey)) {
      return;
    }

    try {
      onRequestEdit(revId);
    } finally {
      requestAnimationFrame(() =>
        endAction(actionKey)
      );
    }
  };

  const handleSave = async (revId) => {
    if (
      locked ||
      savingId === revId ||
      typeof onSave !== "function"
    ) {
      return;
    }

    const actionKey = `save:${revId}`;

    if (!beginAction(actionKey)) {
      return;
    }

    try {
      setSavingId(revId);
      await onSave(revId);
    } catch (error) {
      alert(error?.message || "Save failed.");
    } finally {
      setSavingId(null);
      endAction(actionKey);
    }
  };

  const handlePreviewPdf = async (revId) => {
    if (
      locked ||
      blocking ||
      previewingId === revId ||
      typeof onPreviewPdf !== "function"
    ) {
      return;
    }

    const actionKey = `preview-pdf:${revId}`;

    if (!beginAction(actionKey)) {
      return;
    }

    try {
      setPreviewingId(revId);
      await onPreviewPdf(revId);
    } catch (error) {
      alert(error?.message || "PDF preview failed.");
    } finally {
      setPreviewingId(null);
      endAction(actionKey);
    }
  };

  const openFinalize = (revId) => {
    if (
      locked ||
      blocking ||
      typeof onFinalize !== "function"
    ) {
      return;
    }

    const actionKey = `open-finalize:${revId}`;

    if (!beginAction(actionKey)) {
      return;
    }

    setFinalizeForId(revId);
    setFinalizeOpen(true);

    requestAnimationFrame(() =>
      endAction(actionKey)
    );
  };

  const closeFinalize = () => {
    if (blocking) {
      return;
    }

    setFinalizeOpen(false);
    setFinalizeForId(null);
  };

  /**
   * The Finalize modal previews the exact revision selected when the user
   * clicked Finalize. The Editor page remains the owner of PDF generation.
   *
   * Errors are allowed to propagate so DocFinalizeModal can render them
   * inside the in-app dialog instead of showing a browser alert.
   */
  const previewBeforeFinalize = async () => {
    if (
      !finalizeForId ||
      blocking ||
      typeof onPreviewPdf !== "function"
    ) {
      throw new Error(
        "PDF preview is not available for the selected revision."
      );
    }

    const revisionId = finalizeForId;
    const actionKey = `finalize-preview-pdf:${revisionId}`;

    if (!beginAction(actionKey)) {
      throw new Error(
        "A PDF preview is already being prepared."
      );
    }

    try {
      setPreviewingId(revisionId);
      return await onPreviewPdf(revisionId);
    } finally {
      setPreviewingId(null);
      endAction(actionKey);
    }
  };

  /**
   * Finalization is owned completely by DocWriterEditorPage.
   *
   * This component only sends:
   * - the exact clicked revision ID
   * - the selected export format
   */
  const finalizeAndExport = async (format) => {
    if (
      !finalizeForId ||
      blocking ||
      typeof onFinalize !== "function"
    ) {
      return;
    }

    const revisionId = finalizeForId;
    const actionKey = `finalize:${revisionId}`;

    if (!beginAction(actionKey)) {
      return;
    }

    try {
      setFinalizingId(revisionId);
      setBlocking(true);

      await onFinalize(revisionId, format);

      setFinalizeOpen(false);
      setFinalizeForId(null);
    } catch (error) {
      alert(error?.message || "Finalize failed.");
    } finally {
      setBlocking(false);
      setFinalizingId(null);
      endAction(actionKey);
    }
  };

  // ---------- UI helpers ----------

  const cleanLabel = (label) => {
    const labelText = String(label || "");
    const dashIndex = labelText.indexOf("—");

    return dashIndex >= 0
      ? labelText.slice(0, dashIndex).trim()
      : labelText;
  };

  const firstLine = (value) =>
    String(value || "").split(/\r?\n/)[0];

  // ---------- Styles ----------

  const BTN_H = 26;
  const FINAL_H = 30;
  const BTN_FONT = 11;
  const FINAL_FONT = 11;
  const GRID_GAP = 4;

  const s = {
    wrap: {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 12,
      background: "#fff",
      display: "grid",
      gap: 10,
    },

    headerBlock: {
      display: "grid",
      gap: 8,
      marginBottom: 8,
    },

    title: {
      fontWeight: 800,
      fontSize: 14,
    },

    list: {
      display: "grid",
      gap: 8,
      marginTop: 2,
    },

    row: (active) => ({
      border:
        "1px solid " +
        (active ? "#60a5fa" : "#e5e7eb"),
      borderRadius: 10,
      background: active ? "#eff6ff" : "#fafafa",
      padding: "10px",
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 8,
      alignItems: "center",
    }),

    left: {
      minWidth: 0,
    },

    label: {
      fontWeight: 700,
      wordBreak: "break-word",
    },

    meta: {
      fontSize: 12,
      color: "#6b7280",
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      marginTop: 4,
    },

    chip: (kind) => ({
      fontSize: 11,
      padding: "2px 8px",
      borderRadius: 999,
      border: "1px solid #e5e7eb",
      background:
        kind === "final"
          ? "#dcfce7"
          : kind === "draft"
          ? "#e0e7ff"
          : "#fef3c7",
      color: "#111827",
      whiteSpace: "nowrap",
    }),

    changed: {
      fontSize: 11,
      padding: "2px 8px",
      borderRadius: 999,
      border: "1px solid #fde68a",
      background: "#fffbeb",
      whiteSpace: "nowrap",
    },

    actionsGrid: {
      display: "grid",
      gridTemplateColumns:
        "repeat(2, minmax(96px, 1fr))",
      gap: GRID_GAP,
      minWidth: 196,
      justifyItems: "stretch",
      alignItems: "stretch",
    },

    pressFx: {
      transition:
        "transform 60ms ease, box-shadow 60ms ease",
    },

    pressed: {
      transform: "translateY(1px)",
      boxShadow:
        "inset 0 2px 4px rgba(0,0,0,0.18)",
    },

    blockBtn: {
      width: "100%",
      height: BTN_H,
      lineHeight: `${BTN_H}px`,
      padding: "0 10px",
      borderRadius: 6,
      fontSize: BTN_FONT,
      fontWeight: 800,
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxSizing: "border-box",

      // Faster and more reliable touch/click behavior.
      touchAction: "manipulation",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
    },

    viewBtn: {
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
    },

    restoreBtn: {
      border: "1px solid #2563eb",
      background: "#2563eb",
      color: "#fff",
    },

    editBtn: {
      border: "1px solid #4f46e5",
      background: "#4f46e5",
      color: "#fff",
    },

    saveBtn: {
      border: "1px solid #f59e0b",
      background: "#f59e0b",
      color: "#fff",
    },

    previewBtn: {
      border: "1px solid #0f766e",
      background: "#0f766e",
      color: "#fff",
      height: FINAL_H,
      lineHeight: `${FINAL_H}px`,
      padding: "0 12px",
      borderRadius: 8,
      fontSize: FINAL_FONT,
      boxSizing: "border-box",
      boxShadow: "0 1px 2px rgba(15, 118, 110, 0.18)",
    },

    finalizeBtn: {
      border: "1px solid #111827",
      background: "#111827",
      color: "#fff",
      height: FINAL_H,
      lineHeight: `${FINAL_H}px`,
      padding: "0 12px",
      borderRadius: 8,
      fontSize: FINAL_FONT,
      boxSizing: "border-box",
      boxShadow: "0 1px 2px rgba(17, 24, 39, 0.18)",
    },

    empty: {
      fontSize: 13,
      color: "#6b7280",
    },

    confirmBar: {
      gridColumn: "1 / -1",
      display: "flex",
      gap: 8,
      alignItems: "center",
      background: "#fff",
      borderTop: "1px dashed #e5e7eb",
      paddingTop: 8,
    },

    renameRow: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      marginTop: 8,
    },

    renameInput: {
      flex: 1,
      minWidth: 160,
      padding: "6px 8px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
    },

    moreWrap: {
      position: "relative",
    },

    moreMenu: {
      position: "absolute",
      right: 0,
      top: "calc(100% + 6px)",
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
      padding: 8,
      zIndex: 5,
      display: "grid",
      gap: 6,
      minWidth: 180,
    },

    menuItem: {
      padding: "6px 8px",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      border: "1px solid transparent",
    },

    danger: {
      color: "#b91c1c",
    },

    disabled: {
      opacity: 0.6,
      cursor: "not-allowed",
      filter: "grayscale(0.1)",
    },

    responsiveCss: `
      @media (max-width: 860px) {
        .doc-revision-history .doc-revision-row {
          grid-template-columns: minmax(0, 1fr) !important;
          align-items: stretch !important;
        }

        .doc-revision-history .doc-revision-actions {
          width: 100% !important;
          min-width: 0 !important;
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }
      }

      @media (max-width: 620px) {
        .doc-revision-history .doc-revision-actions {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 6px !important;
        }

        .doc-revision-history .doc-revision-confirm {
          flex-wrap: wrap !important;
          align-items: flex-start !important;
        }

        .doc-revision-history .doc-revision-confirm > strong {
          width: 100% !important;
        }

        .doc-revision-history .doc-revision-rename {
          flex-wrap: wrap !important;
        }

        .doc-revision-history .doc-revision-rename input {
          flex-basis: 100% !important;
          min-width: 100% !important;
        }
      }
    `,
  };

  // ---------- Restore confirmation ----------

  const requestRestore = (id) => {
    if (
      locked ||
      typeof onRestore !== "function"
    ) {
      return;
    }

    const actionKey = `request-restore:${id}`;

    if (!beginAction(actionKey)) {
      return;
    }

    setConfirmRestore(id);
    setOpenMenuId(null);

    requestAnimationFrame(() =>
      endAction(actionKey)
    );
  };

  const doRestore = () => {
    if (
      !confirmRestore ||
      locked ||
      typeof onRestore !== "function"
    ) {
      setConfirmRestore(null);
      return;
    }

    const revisionId = confirmRestore;
    const actionKey = `restore:${revisionId}`;

    if (!beginAction(actionKey)) {
      return;
    }

    setConfirmRestore(null);

    try {
      onRestore(revisionId);
    } finally {
      requestAnimationFrame(() =>
        endAction(actionKey)
      );
    }
  };

  return (
    <div
      className="doc-revision-history"
      style={s.wrap}
      data-revision-panel="true"
    >
      <style>{s.responsiveCss}</style>
      {items.length === 0 ? (
        <div style={s.empty}>
          No revisions yet.
        </div>
      ) : (
        <div style={s.list}>
          {items.map((item) => {
            const active =
              item.id === currentId;

            const awaiting =
              confirmRestore === item.id;

            const isRenaming =
              renamingId === item.id;

            const isSaving =
              savingId === item.id;

            const isPreviewing =
              previewingId === item.id;

            const isFinalizing =
              finalizingId === item.id;

            const kView =
              `${item.id}:view`;

            const kRestore =
              `${item.id}:restore`;

            const kEdit =
              `${item.id}:edit`;

            const kSave =
              `${item.id}:save`;

            const kPreview =
              `${item.id}:preview-pdf`;

            const kFinalize =
              `${item.id}:finalize`;

            const labelText =
              cleanLabel(item.label);

            const summaryTopLine =
              firstLine(item.summary);

            return (
              <div
                key={item.id}
                className="doc-revision-row"
                style={s.row(active)}
              >
                {/* Left */}
                <div style={s.left}>
                  {!isRenaming ? (
                    <>
                      <div style={s.label}>
                        {labelText}
                      </div>

                      <div style={s.meta}>
                        <span
                          style={s.chip(
                            item.type || "revision"
                          )}
                        >
                          {(
                            item.type || "revision"
                          ).toUpperCase()}
                        </span>

                        {item.userEdited ? (
                          <span style={s.changed}>
                            User-edited
                          </span>
                        ) : null}

                        {item.wordCount ? (
                          <span>
                            {item.wordCount.toLocaleString()}{" "}
                            words
                          </span>
                        ) : null}

                        {summaryTopLine ? (
                          <span title={summaryTopLine}>
                            {summaryTopLine}
                          </span>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <div
                      className="doc-revision-rename"
                      style={s.renameRow}
                    >
                      <input
                        style={s.renameInput}
                        value={renameVal}
                        onChange={(event) =>
                          setRenameVal(
                            event.target.value
                          )
                        }
                        placeholder="New title…"
                        autoFocus
                      />

                      <button
                        type="button"
                        style={{
                          ...s.blockBtn,
                          ...s.restoreBtn,
                          ...s.pressFx,
                          ...(isPressed(
                            `${item.id}:rename-save`
                          )
                            ? s.pressed
                            : {}),
                        }}
                        {...pressableHandlers(
                          `${item.id}:rename-save`
                        )}
                        onClick={() => {
                          const nextName =
                            renameVal.trim();

                          const actionKey =
                            `rename:${item.id}`;

                          if (
                            !renamingId ||
                            !nextName ||
                            typeof onRename !==
                              "function" ||
                            !beginAction(actionKey)
                          ) {
                            return;
                          }

                          try {
                            onRename(
                              renamingId,
                              nextName
                            );

                            setRenamingId(null);
                            setRenameVal("");
                          } finally {
                            requestAnimationFrame(() =>
                              endAction(actionKey)
                            );
                          }
                        }}
                      >
                        Save
                      </button>

                      <button
                        type="button"
                        style={{
                          ...s.blockBtn,
                          ...s.viewBtn,
                          ...s.pressFx,
                          ...(isPressed(
                            `${item.id}:rename-cancel`
                          )
                            ? s.pressed
                            : {}),
                        }}
                        {...pressableHandlers(
                          `${item.id}:rename-cancel`
                        )}
                        onClick={() => {
                          setRenamingId(null);
                          setRenameVal("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Right actions */}
                <div
                  className="doc-revision-actions"
                  style={s.actionsGrid}
                >
                  {/* Row 1 */}
                  {typeof onView === "function" ? (
                    <button
                      type="button"
                      style={{
                        ...s.blockBtn,
                        ...s.viewBtn,
                        ...s.pressFx,
                        ...(isPressed(kView)
                          ? s.pressed
                          : {}),
                      }}
                      {...pressableHandlers(kView)}
                      onClick={() => {
                        const actionKey =
                          `view:${item.id}`;

                        if (!beginAction(actionKey)) {
                          return;
                        }

                        try {
                          onView(item.id);
                        } finally {
                          requestAnimationFrame(() =>
                            endAction(actionKey)
                          );
                        }
                      }}
                      title="View the saved version in read-only mode without changing your unsaved edits"
                    >
                      View
                    </button>
                  ) : (
                    <span />
                  )}

                  {typeof onRestore === "function" ? (
                    <button
                      type="button"
                      style={{
                        ...s.blockBtn,
                        ...s.restoreBtn,
                        ...s.pressFx,
                        ...(locked
                          ? s.disabled
                          : {}),
                        ...(isPressed(kRestore)
                          ? s.pressed
                          : {}),
                      }}
                      {...pressableHandlers(kRestore)}
                      disabled={locked}
                      onClick={() =>
                        requestRestore(item.id)
                      }
                      title={
                        locked
                          ? "Document is finalized and locked"
                          : "Discard unsaved edits and reload the saved version"
                      }
                    >
                      Restore
                    </button>
                  ) : (
                    <span />
                  )}

                  {/* Row 2 */}
                  <button
                    type="button"
                    style={{
                      ...s.blockBtn,
                      ...s.editBtn,
                      ...s.pressFx,
                      ...(locked ||
                      typeof onRequestEdit !==
                        "function"
                        ? s.disabled
                        : {}),
                      ...(isPressed(kEdit)
                        ? s.pressed
                        : {}),
                    }}
                    {...pressableHandlers(kEdit)}
                    disabled={
                      locked ||
                      typeof onRequestEdit !==
                        "function"
                    }
                    onClick={() =>
                      handleEdit(item.id)
                    }
                    title={
                      locked
                        ? "Document is finalized and locked"
                        : typeof onRequestEdit !==
                          "function"
                        ? "Edit is not available"
                        : "Open this version in the editor so you can make changes"
                    }
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    style={{
                      ...s.blockBtn,
                      ...s.saveBtn,
                      ...s.pressFx,
                      ...(locked ||
                      isSaving ||
                      typeof onSave !== "function"
                        ? s.disabled
                        : {}),
                      ...(isPressed(kSave)
                        ? s.pressed
                        : {}),
                    }}
                    {...pressableHandlers(kSave)}
                    disabled={
                      locked ||
                      isSaving ||
                      typeof onSave !== "function"
                    }
                    onClick={() =>
                      handleSave(item.id)
                    }
                    title={
                      locked
                        ? "Document is finalized and locked"
                        : typeof onSave !== "function"
                        ? "Save is not available"
                        : "Save the current canvas content to this revision"
                    }
                  >
                    {isSaving
                      ? "Saving…"
                      : "Save"}
                  </button>

                  {/* Row 3 */}
                  <button
                    type="button"
                    style={{
                      ...s.blockBtn,
                      ...s.previewBtn,
                      ...s.pressFx,
                      ...(locked ||
                      blocking ||
                      isPreviewing ||
                      typeof onPreviewPdf !== "function"
                        ? s.disabled
                        : {}),
                      ...(isPressed(kPreview)
                        ? s.pressed
                        : {}),
                    }}
                    {...pressableHandlers(kPreview)}
                    disabled={
                      locked ||
                      blocking ||
                      isPreviewing ||
                      typeof onPreviewPdf !== "function"
                    }
                    aria-busy={isPreviewing}
                    onClick={() =>
                      handlePreviewPdf(item.id)
                    }
                    title={
                      locked
                        ? "Document is finalized and locked"
                        : typeof onPreviewPdf !==
                          "function"
                        ? "PDF preview is not available"
                        : "Generate a temporary backend PDF from the current canvas without saving or finalizing"
                    }
                  >
                    {isPreviewing
                      ? "Preparing…"
                      : "Preview PDF"}
                  </button>

                  <button
                    type="button"
                    style={{
                      ...s.blockBtn,
                      ...s.finalizeBtn,
                      ...s.pressFx,
                      ...(locked ||
                      isFinalizing ||
                      typeof onFinalize !== "function"
                        ? s.disabled
                        : {}),
                      ...(isPressed(kFinalize)
                        ? s.pressed
                        : {}),
                    }}
                    {...pressableHandlers(kFinalize)}
                    disabled={
                      locked ||
                      isFinalizing ||
                      typeof onFinalize !== "function"
                    }
                    onClick={() =>
                      openFinalize(item.id)
                    }
                    title={
                      locked
                        ? "Document is already finalized and locked"
                        : typeof onFinalize !==
                          "function"
                        ? "Finalize is not available"
                        : "Save, permanently lock, and export this document"
                    }
                  >
                    {isFinalizing
                      ? "Working…"
                      : "Finalize"}
                  </button>
                </div>

                {/* Restore confirmation */}
                {awaiting && (
                  <div
                    className="doc-revision-confirm"
                    style={s.confirmBar}
                  >
                    <strong>
                      Discard unsaved edits and reload the saved version?
                    </strong>

                    <button
                      type="button"
                      style={{
                        ...s.blockBtn,
                        width: "auto",
                        ...s.restoreBtn,
                        ...s.pressFx,
                        ...(isPressed(
                          `${item.id}:confirm-yes`
                        )
                          ? s.pressed
                          : {}),
                      }}
                      {...pressableHandlers(
                        `${item.id}:confirm-yes`
                      )}
                      onClick={doRestore}
                      title="Confirm restore and discard unsaved canvas edits"
                    >
                      Yes, restore
                    </button>

                    <button
                      type="button"
                      style={{
                        ...s.blockBtn,
                        width: "auto",
                        ...s.viewBtn,
                        ...s.pressFx,
                        ...(isPressed(
                          `${item.id}:confirm-cancel`
                        )
                          ? s.pressed
                          : {}),
                      }}
                      {...pressableHandlers(
                        `${item.id}:confirm-cancel`
                      )}
                      onClick={() =>
                        setConfirmRestore(null)
                      }
                      title="Keep the current unsaved canvas edits"
                    >
                      Cancel
                    </button>

                    {locked ? (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#b91c1c",
                        }}
                      >
                        (Locked)
                      </span>
                    ) : null}
                  </div>
                )}

                {/* Optional More menu */}
                {(typeof onRename === "function" ||
                  typeof onExport === "function" ||
                  typeof onDelete === "function") && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div style={s.moreWrap}>
                      <button
                        type="button"
                        style={{
                          ...s.blockBtn,
                          width: "auto",
                          ...s.viewBtn,
                          ...s.pressFx,
                          ...(isPressed(
                            `${item.id}:more`
                          )
                            ? s.pressed
                            : {}),
                        }}
                        {...pressableHandlers(
                          `${item.id}:more`
                        )}
                        onClick={() =>
                          setOpenMenuId((value) =>
                            value === item.id
                              ? null
                              : item.id
                          )
                        }
                        aria-haspopup="menu"
                        aria-expanded={
                          openMenuId === item.id
                        }
                        title="More revision actions"
                      >
                        More ▾
                      </button>

                      {openMenuId === item.id && (
                        <div
                          role="menu"
                          style={s.moreMenu}
                        >
                          {typeof onRename ===
                            "function" && (
                            <div
                              role="menuitem"
                              style={s.menuItem}
                              onClick={() => {
                                setRenamingId(item.id);
                                setRenameVal(
                                  cleanLabel(item.label)
                                );
                                setOpenMenuId(null);
                              }}
                            >
                              Rename
                            </div>
                          )}

                          {typeof onExport ===
                            "function" && (
                            <>
                              <div
                                role="menuitem"
                                style={s.menuItem}
                                onClick={() => {
                                  onExport(
                                    item.id,
                                    "pdf"
                                  );

                                  setOpenMenuId(null);
                                }}
                              >
                                Export as PDF
                              </div>

                              <div
                                role="menuitem"
                                style={s.menuItem}
                                onClick={() => {
                                  onExport(
                                    item.id,
                                    "docx"
                                  );

                                  setOpenMenuId(null);
                                }}
                              >
                                Export as DOCX
                              </div>

                              <div
                                role="menuitem"
                                style={s.menuItem}
                                onClick={() => {
                                  onExport(
                                    item.id,
                                    "text"
                                  );

                                  setOpenMenuId(null);
                                }}
                              >
                                Export as TXT
                              </div>
                            </>
                          )}

                          {typeof onDelete ===
                            "function" && (
                            <div
                              role="menuitem"
                              style={{
                                ...s.menuItem,
                                color: "#b91c1c",
                              }}
                              onClick={() => {
                                setOpenMenuId(null);
                                onDelete(item.id);
                              }}
                            >
                              Delete
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Finalize Modal */}
      <DocFinalizeModal
        open={finalizeOpen}
        onClose={closeFinalize}
        onPreviewPdf={previewBeforeFinalize}
        onFinalizeAndExport={finalizeAndExport}
        defaultFormat="pdf"
        subject={String(
          items.find(
            (item) => item.id === finalizeForId
          )?.label || ""
        )
          .split("—")[0]
          .trim()}
      />

      {/* Blocking overlay during finalize */}
      {blocking && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(1px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            color: "#111827",
          }}
          aria-label="Finalizing, please wait"
        >
          Finalizing…
        </div>
      )}
    </div>
  );
};

export default DocRevisionHistory;
